import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatApiDate } from '../utils/date';

function DetalleBoleta({
  boleta,
  onClose,
  onDescargarPdf,
  descargandoPdf,
  onDescargarFacturaPdf,
  descargandoFacturaPdf,
  onCrearPlanPago,
}) {
  const navigate = useNavigate();
  
  if (!boleta) return null;

  const formatearFecha = (fecha) => {
    return formatApiDate(fecha, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatearMonto = (monto) => {
    return parseFloat(monto || 0).toFixed(2);
  };

  const formatearPeriodo = (mes, anio) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${meses[mes - 1]} ${anio}`;
  };

  const estadoFacturaInfo = (estado) => {
    switch (estado) {
      case 'emitida':
        return { label: 'Emitida', className: 'bg-green-100 text-green-700' };
      case 'error':
        return { label: 'Error', className: 'bg-red-100 text-red-700' };
      case 'pendiente':
        return { label: 'Pendiente', className: 'bg-amber-100 text-amber-700' };
      default:
        return { label: 'Sin emitir', className: 'bg-gray-100 text-gray-600' };
    }
  };

  const estadoBoletaInfo = (estado) => {
    switch (estado) {
      case 'pagada':
        return { label: 'Pagada', className: 'bg-green-100 text-green-700' };
      case 'procesando':
        return { label: 'En proceso', className: 'bg-blue-100 text-blue-700' };
      case 'pendiente':
        return { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-700' };
      case 'vencida':
        return { label: 'Vencida', className: 'bg-red-100 text-red-700' };
      case 'plan_pago':
        return { label: 'Plan de pago', className: 'bg-indigo-100 text-indigo-700' };
      default:
        return { label: estado, className: 'bg-gray-100 text-gray-600' };
    }
  };

  const facturaNumero = () => {
    if (!boleta.factura_punto_venta || !boleta.factura_numero) return 'N/A';
    const pto = String(boleta.factura_punto_venta).padStart(4, '0');
    const nro = String(boleta.factura_numero).padStart(8, '0');
    return `${pto}-${nro}`;
  };

  const facturaDisponible = boleta.factura_estado === 'emitida';
  const facturaEstado = estadoFacturaInfo(boleta.factura_estado);
  const estadoBoleta = estadoBoletaInfo(boleta.estado);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#101c22] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-[#101c22] border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-[#111618] dark:text-white">
              Detalle de Boleta
            </h2>
            <span className={`inline-flex mt-2 px-3 py-1 rounded-full text-xs font-semibold ${estadoBoleta.className}`}>
              {estadoBoleta.label}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Info de la Factura */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-[#617c89] dark:text-gray-400 mb-1">Período de Facturación</p>
                <p className="text-lg font-bold text-[#111618] dark:text-white">
                  {formatearPeriodo(boleta.mes, boleta.anio)}
                </p>
              </div>
              <div>
                <p className="text-sm text-[#617c89] dark:text-gray-400 mb-1">Fecha de Vencimiento</p>
                <p className="text-lg font-bold text-[#111618] dark:text-white">
                  {formatearFecha(boleta.fechaVencimiento)}
                </p>
              </div>
              <div>
                <p className="text-sm text-[#617c89] dark:text-gray-400 mb-1">Padrón N°</p>
                <p className="text-lg font-bold text-[#111618] dark:text-white">
                  {boleta.usuario?.padron || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-[#617c89] dark:text-gray-400 mb-1">Zona</p>
                <p className="text-lg font-bold text-[#111618] dark:text-white">
                  {boleta.usuario?.zona?.nombre || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Facturación electrónica */}
          <div className="border border-[#dbe2e6] dark:border-gray-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#111618] dark:text-white">
                Facturación electrónica (ARCA)
              </h3>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${facturaEstado.className}`}>
                {facturaEstado.label}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs uppercase text-[#617c89] dark:text-gray-400">Punto de venta y número</p>
                <p className="text-base font-semibold text-[#111618] dark:text-white">
                  {facturaNumero()}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-[#617c89] dark:text-gray-400">CAE</p>
                <p className="text-base font-semibold text-[#111618] dark:text-white">
                  {boleta.factura_cae || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-[#617c89] dark:text-gray-400">Fecha emisión</p>
                <p className="text-base font-semibold text-[#111618] dark:text-white">
                  {boleta.factura_fecha_emision ? formatearFecha(boleta.factura_fecha_emision) : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-[#617c89] dark:text-gray-400">Vencimiento CAE</p>
                <p className="text-base font-semibold text-[#111618] dark:text-white">
                  {boleta.factura_cae_vto ? formatearFecha(boleta.factura_cae_vto) : 'N/A'}
                </p>
              </div>
            </div>
            {boleta.factura_estado === 'error' && boleta.factura_error && (
              <div className="mt-4 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg">
                {boleta.factura_error}
              </div>
            )}
          </div>

          {boleta.estado === 'plan_pago' && (
            <div className="border border-indigo-200 bg-indigo-50 dark:border-indigo-900/30 dark:bg-indigo-900/20 rounded-lg p-6">
              <h3 className="text-lg font-bold text-indigo-800 dark:text-indigo-200 mb-2">
                Plan de pago activo
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-indigo-900 dark:text-indigo-100">
                <p><span className="font-semibold">Cuotas:</span> {boleta.plan_pago_cantidad_cuotas || '-'}</p>
                <p><span className="font-semibold">Interés:</span> {boleta.plan_pago_interes_porcentaje ?? 0}%</p>
                <p><span className="font-semibold">Total plan:</span> ${formatearMonto(boleta.plan_pago_monto_total)}</p>
                <p><span className="font-semibold">Cuota:</span> ${formatearMonto(boleta.plan_pago_monto_cuota)}</p>
                <p><span className="font-semibold">Inicio:</span> {boleta.plan_pago_inicio_mes ? `${boleta.plan_pago_inicio_mes}/${boleta.plan_pago_inicio_anio}` : '-'}</p>
              </div>
            </div>
          )}

          {/* Información de Medidor */}
          {boleta.lectura && (
            <div className="border border-[#dbe2e6] dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-bold text-[#111618] dark:text-white mb-4">
                Información del Medidor
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-[#617c89] dark:text-gray-400 mb-1">Lectura Anterior</p>
                  <p className="text-2xl font-bold text-[#111618] dark:text-white">
                    {boleta.lectura.lecturaAnterior} m³
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#617c89] dark:text-gray-400 mb-1">Lectura Actual</p>
                  <p className="text-2xl font-bold text-[#111618] dark:text-white">
                    {boleta.lectura.lecturaActual} m³
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#617c89] dark:text-gray-400 mb-1">Consumo del Período</p>
                  <p className="text-2xl font-bold text-[#007BFF]">
                    {formatearMonto(boleta.consumo_m3)} m³
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Desglose de Conceptos */}
          <div className="border border-[#dbe2e6] dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-800/50 px-6 py-3 border-b border-[#dbe2e6] dark:border-gray-700">
              <h3 className="text-lg font-bold text-[#111618] dark:text-white">
                Desglose de Conceptos
              </h3>
            </div>
            <div className="p-6 space-y-3">
              {/* Servicio Base */}
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-[#111618] dark:text-gray-300">Servicio de Agua Potable</span>
                <span className="font-semibold text-[#111618] dark:text-white">
                  ${formatearMonto(boleta.monto_servicio_base)}
                </span>
              </div>

              {/* Consumo Escalonado */}
              {boleta.tiene_medidor && parseFloat(boleta.consumo_m3) > 0 && (
                <>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-2">
                    <p className="text-sm font-semibold text-[#007BFF] mb-2">Consumo por Escalas</p>
                    
                    {boleta.desglose_consumo && boleta.desglose_consumo.length > 0 ? (
                      <>
                        {boleta.desglose_consumo.map((escala, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-[#617c89] dark:text-gray-400">
                              Consumo {escala.desde}-{escala.hasta || '∞'} m³: {parseFloat(escala.consumo_m3).toFixed(2)} m³
                              {escala.precio_por_m3 > 0 && ` × $${formatearMonto(escala.precio_por_m3)}`}
                            </span>
                            <span className="text-[#111618] dark:text-gray-300">
                              {escala.precio_por_m3 === 0 ? 'Incluido en servicio base' : `$${formatearMonto(escala.subtotal)}`}
                            </span>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Sin desglose disponible
                      </div>
                    )}

                    <div className="flex justify-between pt-2 border-t border-blue-200 dark:border-blue-800">
                      <span className="font-semibold text-[#111618] dark:text-white">Total Consumo</span>
                      <span className="font-semibold text-[#111618] dark:text-white">
                        ${formatearMonto(boleta.monto_consumo)}
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Subtotal */}
              <div className="flex justify-between items-center py-2 border-t border-gray-300 dark:border-gray-600 font-semibold">
                <span className="text-[#111618] dark:text-white">Subtotal</span>
                <span className="text-[#111618] dark:text-white">
                  ${formatearMonto(boleta.subtotal)}
                </span>
              </div>

              {/* Cargos Extras */}
              {parseFloat(boleta.total_cargos_extras) > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-[#111618] dark:text-gray-300">Cargos Extras</span>
                  <span className="font-semibold text-[#111618] dark:text-white">
                    ${formatearMonto(boleta.total_cargos_extras)}
                  </span>
                </div>
              )}

              {/* Total Final */}
              <div className="flex justify-between items-center py-4 border-t-2 border-gray-300 dark:border-gray-600">
                <span className="text-xl font-bold text-[#111618] dark:text-white">TOTAL</span>
                <span className="text-2xl font-bold text-[#007BFF]">
                  ${formatearMonto(boleta.total)}
                </span>
              </div>
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="flex flex-col sm:flex-row gap-3">
            {boleta.estado === 'pendiente' && (
              <button 
                onClick={() => navigate(`/cliente/pagar-boleta/${boleta.id}`)}
                className="flex-1 bg-[#007BFF] text-white font-medium py-3 rounded-lg hover:bg-[#007BFF]/90 transition-colors"
              >
                Pagar Ahora
              </button>
            )}
            {onDescargarPdf && (
              <button
                onClick={() => onDescargarPdf(boleta)}
                disabled={descargandoPdf}
                className="flex-1 border border-[#007BFF] text-[#007BFF] font-medium py-3 rounded-lg hover:bg-[#007BFF]/10 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {descargandoPdf ? 'Descargando PDF...' : 'Descargar PDF'}
              </button>
            )}
            {onDescargarFacturaPdf && (
              <button
                onClick={() => onDescargarFacturaPdf(boleta)}
                disabled={descargandoFacturaPdf || !facturaDisponible}
                className="flex-1 border border-emerald-500 text-emerald-600 font-medium py-3 rounded-lg hover:bg-emerald-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {descargandoFacturaPdf
                  ? 'Descargando Factura...'
                  : facturaDisponible
                    ? 'Descargar Factura'
                    : 'Factura pendiente'}
              </button>
            )}
            <button 
              onClick={onClose}
              className="flex-1 bg-gray-200 dark:bg-gray-700 text-[#111618] dark:text-white font-medium py-3 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DetalleBoleta;
